/**
 * Création d'une animation : qui parle (miroir de `animationVoicesSchema`,
 * `supabase/functions/_shared/prompts/animation-generate.ts`).
 *
 *   - monologue : une voix (« Narrateur », « Le prof »…), que plusieurs
 *     personnes peuvent enregistrer (une voix par scène) ;
 *   - dialogue à 2 ou 3 : un scénario (prof / élève, entre potes…), des noms
 *     de rôles modifiables, et des consignes sur le dialogue. Chaque réplique
 *     sera enregistrée par qui tient le rôle (Speaker 1, 2, 3).
 * PUR.
 */

export type VoicesMode = 'monologue' | 'dialogue';

export interface VoiceScenario {
  id: string;
  label: string;
  /** noms proposés pour Speaker 1, 2, 3 */
  roles: readonly [string, string, string];
}

export const VOICE_SCENARIOS: readonly VoiceScenario[] = [
  { id: 'prof-eleve', label: 'Prof / élève', roles: ['Le prof', "L'élève", 'Un autre élève'] },
  { id: 'potes', label: 'Entre potes', roles: ['Léa', 'Tom', 'Sam'] },
  {
    id: 'freres',
    label: 'Grand frère / petite sœur',
    roles: ['Le grand frère', 'La petite sœur', 'Le cousin'],
  },
  { id: 'libre', label: 'Libre', roles: ['Speaker 1', 'Speaker 2', 'Speaker 3'] },
];

export interface VoicesChoice {
  mode: VoicesMode;
  /** 2 ou 3 en dialogue (ignoré en monologue) */
  count: 2 | 3;
  scenario: string;
  /** noms des rôles (Speaker 1, 2, 3) ; en monologue seul le premier compte */
  roles: string[];
  notes: string;
}

export const DEFAULT_VOICES: VoicesChoice = {
  mode: 'monologue',
  count: 2,
  scenario: 'prof-eleve',
  roles: ['Narrateur', "L'élève", 'Un autre élève'],
  notes: '',
};

export const ROLE_MAX = 40;
export const NOTES_MAX = 1000;

/** Change de scénario : les noms proposés remplacent les noms actuels. */
export function withScenario(choice: VoicesChoice, scenarioId: string): VoicesChoice {
  const sc = VOICE_SCENARIOS.find((s) => s.id === scenarioId);
  if (!sc) return choice;
  return { ...choice, scenario: sc.id, roles: [...sc.roles] };
}

/** Passe en monologue, dialogue à 2 ou à 3. */
export function withMode(choice: VoicesChoice, mode: VoicesMode, count: 2 | 3 = 2): VoicesChoice {
  if (mode === 'monologue') {
    const first = choice.mode === 'monologue' ? choice.roles[0] : 'Narrateur';
    return { ...choice, mode, roles: [first ?? 'Narrateur', ...choice.roles.slice(1)] };
  }
  const sc = VOICE_SCENARIOS.find((s) => s.id === choice.scenario) ?? VOICE_SCENARIOS[0]!;
  const roles = choice.mode === 'dialogue' ? [...choice.roles] : [...sc.roles];
  for (let i = 0; i < 3; i++) if (!roles[i]?.trim()) roles[i] = sc.roles[i]!;
  return { ...choice, mode, count, scenario: sc.id, roles };
}

/** Nom de rôle utilisable : une ligne simple, sans formule. */
export function cleanRoleName(name: string): string {
  return name
    .replace(/[$\\{}]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, ROLE_MAX);
}

/** Ce que reçoit `animation-generate` (`voices`) ; rien pour le narrateur par défaut. */
export function voicesBody(
  choice: VoicesChoice,
): { mode: VoicesMode; roles: string[]; notes?: string } | undefined {
  const notes = choice.notes.trim().slice(0, NOTES_MAX);
  if (choice.mode === 'monologue') {
    const role = cleanRoleName(choice.roles[0] ?? '') || 'Narrateur';
    if (role === 'Narrateur' && !notes) return undefined;
    return { mode: 'monologue', roles: [role], ...(notes ? { notes } : {}) };
  }
  const sc = VOICE_SCENARIOS.find((s) => s.id === choice.scenario) ?? VOICE_SCENARIOS[0]!;
  const roles = Array.from(
    { length: choice.count },
    (_, i) => cleanRoleName(choice.roles[i] ?? '') || sc.roles[i]!,
  );
  return { mode: 'dialogue', roles, ...(notes ? { notes } : {}) };
}

/** Deux rôles portent le même nom : la liste des voix serait ambiguë. */
export function duplicateRoleNames(choice: VoicesChoice): boolean {
  if (choice.mode === 'monologue') return false;
  const names = choice.roles.slice(0, choice.count).map((r) => cleanRoleName(r).toLowerCase());
  return new Set(names.filter(Boolean)).size < names.filter(Boolean).length;
}
